/**
 * Eval overview page — `/evals/:evalId`.
 *
 * Aggregate stats over the full Run history (totals + status mix), the
 * latest score summary, and a slim per-entry table whose rows deep-link
 * into `/evals/:evalId/run/:runId` for the full drill-down.
 *
 * Header, alerts, and the Run history sidebar live in `<EvalDetailShell>`.
 */
import { useClient } from '@cortex/react-query';
import {
  GateVerdictSchema,
  type EvalRunResult,
  type Evaluation,
  type EvaluationRunHistoryItem,
  type GateVerdict,
  type ModelConfig,
  type Workflow,
  type WorkflowVersion,
} from '@cortex/types';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { EvalDetailShell } from '../../../components/evals/EvalDetailShell';
import { EvalGraderScores } from '../../../components/evals/EvalGraderScores';
import { EvalGradersAttachment } from '../../../components/evals/EvalGradersAttachment';
import { EvalScorecardGrid } from '../../../components/evals/EvalScorecardGrid';
import { TaskBasedEvalDetail } from '../../../components/evals/TaskBasedEvalDetail';
import {
  Field,
  camelToTitle,
  formatMetric,
  gridStyle,
  readEvalConfig,
  readEvalMode,
  renderGraderModel,
  tdStyle,
  thStyle,
  type IPerEntryResult,
} from '../../../components/evals/eval-detail-shared';
import { EvalRunDetail } from '../../../components/evals/eval-run-detail';
import { routes } from '../../../lib/constants';
import { formatDateTime, formatDuration } from '../../../lib/format';

/** Validate the JSONB verdict through the shared contract — a drifted shape
 *  renders as "pending" rather than NaN percentages. */
function parseGateVerdict(raw: unknown): GateVerdict | null {
  const parsed = GateVerdictSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

export const Route = createFileRoute('/evals/$evalId/')({ component: EvalOverviewPage });

function EvalOverviewPage() {
  const { evalId } = Route.useParams();
  return (
    <EvalDetailShell evalId={evalId}>
      {({ evaluation: e, runEval, isRunning, baselineIds, setBaselineIds, isComparing }) => {
        // : KG evals now run through the task-runner
        // and emit the standard scoreSummary.taskResults shape, so they
        // fall through the same task-based detection below. No dedicated
        // KG branch here — KgScoreCard was retired with the unification.
        const raw = e.scoreSummary as Record<string, unknown> | null;

        // Detect Task-based eval — two signals:
        // 1. scoreSummary already has `taskResults` (completed or partially written)
        // 2. The eval row has `suiteId` or `nTrials` (set at creation, present in
        //    the JSON response even though not declared in the Zod EvaluationSchema)
        //
        // Signal 2 lets us route to the Task-based UI while RUNNING (before
        // scoreSummary is populated), so users see Task-based progress instead
        // of the legacy "No score summary yet" placeholder.
        const hasTaskResults = raw && Array.isArray((raw as { taskResults?: unknown }).taskResults);
        const evalRow = e as unknown as Record<string, unknown>;
        const createdAsTaskBased = !!evalRow.suiteId || evalRow.nTrials != null;
        const isTaskBased = hasTaskResults || createdAsTaskBased;

        if (isTaskBased) {
          const progress = e.progress as {
            completed: number;
            total: number;
            failed: number;
          } | null;

          // If scoreSummary has taskResults (even partial), render the
          // detail view immediately — completed tasks show full results while
          // remaining tasks show as pending. This is the incremental progress UX.
          if (!hasTaskResults) {
            const suiteId = evalRow.suiteId as string | null;
            return (
              <TaskBasedRunningState
                isRunning={isRunning}
                progress={progress}
                status={e.status}
                hasSuite={!!suiteId}
              />
            );
          }

          const evalRunResult = raw as unknown as EvalRunResult;
          const isPartial = !!(raw as Record<string, unknown>)?._partial;
          return (
            <TaskBasedEvalDetail
              result={evalRunResult}
              isRunning={isRunning}
              isPartial={isPartial}
              progress={progress}
              hasGate={!!evalRunResult.suiteId}
              serverGateVerdict={parseGateVerdict(
                (e as unknown as Record<string, unknown>).gateVerdict,
              )}
              evalId={e.id}
              workflowId={e.workflowId ?? undefined}
            />
          );
        }

        return (
          <OverviewBody
            e={e}
            runEval={runEval}
            isRunning={isRunning}
            baselineIds={baselineIds}
            setBaselineIds={setBaselineIds}
            isComparing={isComparing}
          />
        );
      }}
    </EvalDetailShell>
  );
}

// ── Per-entry filter type ──────────────────────────────────────────────────

type EntryFilter = 'all' | 'passed' | 'failed' | 'errored';

function classifyEntry(r: IPerEntryResult): 'passed' | 'failed' | 'errored' {
  if (r.error) return 'errored';
  return r.passed ? 'passed' : 'failed';
}

// ── Main body ──────────────────────────────────────────────────────────────

function OverviewBody({
  e,
  runEval,
  isRunning,
  baselineIds,
  setBaselineIds,
  isComparing,
}: {
  e: Evaluation;
  runEval: () => void;
  isRunning: boolean;
  baselineIds: string[];
  setBaselineIds: (ids: string[]) => void;
  isComparing: boolean;
}) {
  // Metadata fetches that the body used to do are now owned by
  // `<MetadataPanel>` → `<EvalMetadataColumn>` (which fetches its own
  // workflow / versions / run-history per eval). Keep the body small.

  const summary = (e.scoreSummary as Record<string, unknown> | null) ?? null;
  const perEntry: IPerEntryResult[] = Array.isArray(summary?.perEntry)
    ? (summary?.perEntry as IPerEntryResult[])
    : [];
  const mode = readEvalMode(e);
  const isAdvanced = mode === 'advanced';

  const nodeScore = useMemo(() => {
    let passed = 0;
    let total = 0;
    for (const ent of perEntry) {
      for (const r of ent.nodeResults ?? []) {
        total += 1;
        if (r.passed) passed += 1;
      }
    }
    return { passed, total };
  }, [perEntry]);

  // Per-entry filter state
  const [entryFilter, setEntryFilter] = useState<EntryFilter>('all');
  const entryCounts = useMemo(() => {
    const c = { passed: 0, failed: 0, errored: 0 };
    for (const r of perEntry) c[classifyEntry(r)]++;
    return c;
  }, [perEntry]);
  const filteredEntries = useMemo(
    () =>
      entryFilter === 'all' ? perEntry : perEntry.filter((r) => classifyEntry(r) === entryFilter),
    [perEntry, entryFilter],
  );

  return (
    <>
      <MetadataPanel
        currentEvalId={e.id}
        currentWorkflowId={e.workflowId}
        currentTargetKind={e.targetKind ?? 'WORKFLOW'}
        baselineIds={baselineIds}
        setBaselineIds={setBaselineIds}
        isComparing={isComparing}
      />

      {nodeScore.total > 0 ? <NodeLevelHeadline evalId={e.id} score={nodeScore} /> : null}

      {summary && (summary as Record<string, unknown>).judgeAggregates ? (
        <div className="card" style={{ marginBottom: 'var(--sp-3)' }}>
          <div className="card-body">
            <h3 className="t-h4" style={{ marginBottom: 'var(--sp-3)' }}>
              Score cards
            </h3>
            <EvalScorecardGrid evalId={e.id} baselineEvalIds={baselineIds} />
          </div>
        </div>
      ) : null}

      {isAdvanced ? (
        <>
          <div className="card" style={{ marginBottom: 'var(--sp-3)' }}>
            <div className="card-body">
              <h3 className="t-h4" style={{ marginBottom: 'var(--sp-2)' }}>
                Grader aggregates
              </h3>
              <p className="t-xs t-muted" style={{ marginBottom: 'var(--sp-2)' }}>
                What each attached grader said across every ground-truth entry from the most recent
                run.
              </p>
              <EvalGraderScores evalId={e.id} baselineEvalIds={baselineIds} />
            </div>
          </div>

          <EvalGradersAttachment evalId={e.id} />
        </>
      ) : null}

      {summary ? (
        (() => {
          const detEntries = Object.entries(summary).filter(
            ([k]) => k !== 'perEntry' && k !== 'judgeAggregates',
          );
          if (detEntries.length === 0 && !isComparing) return null;
          return isAdvanced ? (
            <details className="card" style={{ marginBottom: 'var(--sp-3)' }}>
              <summary className="card-body" style={{ cursor: 'pointer', listStyle: 'revert' }}>
                <span className="t-h4">Deterministic scoring</span>
                <span className="t-xs t-muted" style={{ marginLeft: 'var(--sp-2)' }}>
                  {isComparing
                    ? 'side-by-side metrics + per-baseline deltas'
                    : 'disposition / urgency / tag F1 from the deterministic scoring path'}
                </span>
              </summary>
              <div className="card-body" style={{ paddingTop: 0 }}>
                {isComparing && baselineIds.length > 0 ? (
                  <ScoreSummaryCompareTable currentEvalId={e.id} baselineEvalIds={baselineIds} />
                ) : (
                  <div style={gridStyle}>
                    {detEntries.map(([k, v]) => (
                      <Field key={k} label={camelToTitle(k)}>
                        {formatMetric(v)}
                      </Field>
                    ))}
                  </div>
                )}
              </div>
            </details>
          ) : (
            <div className="card" style={{ marginBottom: 'var(--sp-3)' }}>
              <div className="card-body">
                <h3 className="t-h4" style={{ marginBottom: 'var(--sp-2)' }}>
                  Score summary
                </h3>
                <p className="t-xs t-muted" style={{ marginBottom: 'var(--sp-2)' }}>
                  {isComparing
                    ? 'Side-by-side metrics for the picked evaluations, with deltas vs each baseline.'
                    : 'Deterministic + LLM-grader metrics from the most recent execution.'}
                </p>
                {isComparing && baselineIds.length > 0 ? (
                  <ScoreSummaryCompareTable currentEvalId={e.id} baselineEvalIds={baselineIds} />
                ) : (
                  <div style={gridStyle}>
                    {detEntries.map(([k, v]) => (
                      <Field key={k} label={camelToTitle(k)}>
                        {formatMetric(v)}
                      </Field>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })()
      ) : (
        <div className="card" style={{ marginBottom: 'var(--sp-3)' }}>
          <div className="card-body">
            <h3 className="t-h4" style={{ marginBottom: 'var(--sp-2)' }}>
              No score summary yet
            </h3>
            <p className="t-sm t-muted">
              Click <strong>Run evaluation</strong> above to fan out workflow runs.
            </p>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={runEval}
              disabled={isRunning}
            >
              {isRunning ? 'Running…' : 'Run evaluation'}
            </button>
          </div>
        </div>
      )}

      {/* ── Per-entry runs with filter bar (item 5) ── */}
      {perEntry.length > 0 ? (
        <div className="card" style={{ marginBottom: 'var(--sp-3)' }}>
          <div className="card-body">
            <h3 className="t-h4" style={{ marginBottom: 'var(--sp-2)' }}>
              Per-entry runs
            </h3>
            <p className="t-xs t-muted" style={{ marginBottom: 'var(--sp-2)' }}>
              Click a row to expand input / expected / actual and the grader&apos;s reasoning.
            </p>
            {/* Filter bar */}
            <div style={{ display: 'flex', gap: 'var(--sp-1)', marginBottom: 'var(--sp-2)' }}>
              {(
                [
                  ['all', perEntry.length],
                  ['passed', entryCounts.passed],
                  ['failed', entryCounts.failed],
                  ['errored', entryCounts.errored],
                ] as const
              ).map(([key, count]) => (
                <button
                  key={key}
                  type="button"
                  className={`btn btn--sm ${entryFilter === key ? 'btn--primary' : 'btn--ghost'}`}
                  onClick={() => setEntryFilter(key)}
                >
                  {key.charAt(0).toUpperCase() + key.slice(1)} ({count})
                </button>
              ))}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
                  <th style={{ ...thStyle, width: 24 }} />
                  <th style={thStyle}>Entry</th>
                  <th style={thStyle}>Run</th>
                  <th style={thStyle}>Error</th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.map((r, i) => (
                  <PerEntryRow key={r.entryId ?? i} result={r} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </>
  );
}

// ── Compare metadata panel ─────────────────────────────────────────────────

/**
 * Series colors come from the Formant UI kit's chart palette
 * (`--chart-1..3` aliased to sage / iris / amber). Keep in sync with
 * EvalScorecardGrid.tsx and EvalGraderScores.tsx.
 */
const currentSeriesColor = 'var(--chart-1, #ACC3B3)';
const baselineSlotColors = ['var(--chart-2, #B5B6FB)', 'var(--chart-3, #E8AB7F)'] as const;

/**
 * Top metadata panel. Hosts the current eval's identity + (when comparing)
 * one column per baseline slot. Each empty slot shows a picker dropdown
 * until the user selects an eval. Compare state lives in the shell, so
 * unmounting this panel doesn't lose the selection.
 */
function MetadataPanel({
  currentEvalId,
  currentWorkflowId,
  currentTargetKind,
  baselineIds,
  setBaselineIds,
  isComparing,
}: {
  currentEvalId: string;
  /** Null for KG- and MEMORY-target evals. */
  currentWorkflowId: string | null;
  /** Disambiguates the two workflow-less kinds for comparison grouping. */
  currentTargetKind: string;
  baselineIds: string[];
  setBaselineIds: (ids: string[]) => void;
  isComparing: boolean;
}) {
  const client = useClient();
  const otherEvals = useQuery({
    enabled: isComparing,
    queryKey: ['evaluations', 'for-compare', 'all'],
    queryFn: () => client.http.get('v1/evaluations?limit=200').json<{ items: Evaluation[] }>(),
  });
  const workflows = useQuery({
    enabled: isComparing,
    queryKey: ['workflows', 'for-compare'],
    queryFn: () => client.http.get('v1/workflows?limit=200').json<{ items: Workflow[] }>(),
  });

  const candidates = (otherEvals.data?.items ?? []).filter(
    (ev) => ev.id !== currentEvalId && ev.status === 'COMPLETED',
  );
  const grouped = useMemo(() => {
    const wfName = new Map<string, string>();
    for (const w of workflows.data?.items ?? []) wfName.set(w.id, w.name);
    const byWf = new Map<string, Evaluation[]>();
    // Group workflow-less evals under per-kind synthetic buckets so they
    // remain visible in the comparison picker without breaking the
    // string-keyed Map — and so MEMORY (recall/nDCG) and KG (citation
    // recall) evals never share a bucket; their metrics aren't comparable.
    const kgGroupKey = '__kg__';
    const memoryGroupKey = '__memory__';
    const syntheticKey = (targetKind: string | undefined) =>
      targetKind === 'MEMORY' ? memoryGroupKey : kgGroupKey;
    for (const c of candidates) {
      const key = c.workflowId ?? syntheticKey(c.targetKind);
      const arr = byWf.get(key) ?? [];
      arr.push(c);
      byWf.set(key, arr);
    }
    return Array.from(byWf.entries())
      .map(([wfId, evs]) => ({
        wfId,
        wfName:
          wfId === kgGroupKey
            ? 'Knowledge Graph evals'
            : wfId === memoryGroupKey
              ? 'Memory evals'
              : (wfName.get(wfId) ?? wfId.slice(0, 12)),
        evs: evs.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
      }))
      .sort((a, b) => {
        // The current eval's own group bubbles to the top.
        const currentKey = currentWorkflowId ?? syntheticKey(currentTargetKind);
        if (a.wfId === currentKey) return -1;
        if (b.wfId === currentKey) return 1;
        return a.wfName.localeCompare(b.wfName);
      });
  }, [candidates, workflows.data, currentWorkflowId, currentTargetKind]);

  const setSlot = (slot: 0 | 1, id: string | null) => {
    const next = [...baselineIds];
    if (id == null) {
      next.splice(slot, 1);
    } else {
      next[slot] = id;
    }
    const seen = new Set<string>();
    const cleaned = next.filter((x): x is string => {
      if (!x || seen.has(x)) return false;
      seen.add(x);
      return true;
    });
    setBaselineIds(cleaned.slice(0, 2));
  };

  if (!isComparing) {
    return (
      <div className="card" style={{ marginBottom: 'var(--sp-3)' }}>
        <div className="card-body">
          <EvalMetadataColumn evalId={currentEvalId} color={currentSeriesColor} role="Current" />
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 'var(--sp-3)',
        marginBottom: 'var(--sp-3)',
      }}
    >
      <div className="card">
        <div className="card-body">
          <EvalMetadataColumn evalId={currentEvalId} color={currentSeriesColor} role="Current" />
        </div>
      </div>
      {([0, 1] as const).map((slot) => {
        const id = baselineIds[slot];
        const color = baselineSlotColors[slot];
        const role = `Baseline ${slot + 1}`;
        return (
          <div key={slot} className="card">
            <div className="card-body">
              {id ? (
                <EvalMetadataColumn
                  evalId={id}
                  color={color}
                  role={role}
                  onClear={() => setSlot(slot, null)}
                />
              ) : (
                <BaselineSlotPicker
                  role={role}
                  color={color}
                  grouped={grouped}
                  emptyMessage={
                    candidates.length === 0 ? 'No other completed evaluations yet' : null
                  }
                  onPick={(picked) => setSlot(slot, picked)}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ColumnHeader({
  color,
  role,
  onClear,
}: {
  color: string;
  role: string;
  onClear?: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 'var(--sp-2)',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: '.08em',
          color: 'var(--text-secondary)',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            width: 14,
            height: 4,
            background: color,
            borderRadius: 2,
          }}
        />
        {role}
      </span>
      {onClear && (
        <button type="button" className="btn btn--ghost btn--sm" onClick={onClear}>
          Clear
        </button>
      )}
    </div>
  );
}

/**
 * Self-contained metadata column for one eval. Fetches its own evaluation,
 * workflow, versions, and run-history via shared react-query keys so the
 * cache lights up across components.
 */
function EvalMetadataColumn({
  evalId,
  color,
  role,
  onClear,
}: {
  evalId: string;
  color: string;
  role: string;
  onClear?: () => void;
}) {
  const client = useClient();
  const evalQ = useQuery({
    queryKey: ['evaluation', evalId],
    queryFn: () => client.http.get(`v1/evaluations/${evalId}`).json<Evaluation>(),
  });
  const workflowId = evalQ.data?.workflowId;
  const workflowQ = useQuery({
    enabled: !!workflowId,
    queryKey: ['workflow', workflowId],
    queryFn: () => client.http.get(`v1/workflows/${workflowId}`).json<Workflow>(),
  });
  const versionsQ = useQuery({
    enabled: !!workflowId,
    queryKey: ['workflow-versions', workflowId],
    queryFn: () => client.http.get(`v1/workflows/${workflowId}/versions`).json<WorkflowVersion[]>(),
  });
  const historyQ = useQuery({
    queryKey: ['eval-run-history', evalId],
    queryFn: () =>
      client.http
        .get(`v1/evaluations/${evalId}/run-history?limit=1`)
        .json<{ items: EvaluationRunHistoryItem[]; total: number }>(),
  });
  // Shared with the outer body's grader-model fetch via the same queryKey.
  const modelsQ = useQuery({
    queryKey: ['model-configs', 'all-for-eval-detail'],
    queryFn: () =>
      client.http.get('v1/model-configs?limit=200').json<{ items: ModelConfig[]; total: number }>(),
  });

  const e = evalQ.data;
  const wf = workflowQ.data;
  const latest = historyQ.data?.items?.[0];
  const evalConfig = e ? readEvalConfig(e) : null;
  const showGraderModel =
    evalConfig?.scoringMethod === 'llm-judge' || evalConfig?.scoringMethod === 'both';
  const versionLabel = (id: string | null | undefined): string | null => {
    if (!id) return null;
    const v = (versionsQ.data ?? []).find((x) => x.id === id);
    return v ? `v${v.versionNumber}` : id.slice(0, 12);
  };

  return (
    <>
      <ColumnHeader color={color} role={role} onClear={onClear} />
      {!e ? (
        <em className="t-muted">Loading…</em>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 'var(--sp-2)',
          }}
        >
          <Field label="Workflow">
            {e.workflowId == null ? (
              <em className="t-muted">— (KG eval)</em>
            ) : wf ? (
              <Link to={routes.workflowDetail(e.workflowId)}>{wf.name}</Link>
            ) : (
              <code className="t-mono">{e.workflowId.slice(0, 12)}</code>
            )}
          </Field>
          <Field label="Status">
            <span className={`fk-badge--${e.status.toLowerCase()}`}>{e.status}</span>
          </Field>
          <Field label="Workflow version">
            {(() => {
              const pinned = versionLabel(e.workflowVersionId);
              if (pinned) return <code className="t-mono">{pinned}</code>;
              const actual = versionLabel(latest?.workflowVersionId);
              if (actual)
                return (
                  <span>
                    <code className="t-mono">{actual}</code>{' '}
                    <span className="t-xs t-muted">(current)</span>
                  </span>
                );
              return <span className="t-muted">Current</span>;
            })()}
          </Field>
          {showGraderModel && (
            <Field label="Grader model">
              {renderGraderModel(evalConfig?.graderModelConfigId, modelsQ.data?.items)}
            </Field>
          )}
          <Field label="Created">{formatDateTime(e.createdAt)}</Field>
          <Field label="Latest run">
            {latest?.createdAt ? formatDateTime(new Date(latest.createdAt)) : '—'}
          </Field>
          <Field label="Duration">{formatDuration(e.startedAt, e.finishedAt)}</Field>
        </div>
      )}
    </>
  );
}

function BaselineSlotPicker({
  role,
  color,
  grouped,
  emptyMessage,
  onPick,
}: {
  role: string;
  color: string;
  grouped: { wfId: string; wfName: string; evs: Evaluation[] }[];
  emptyMessage: string | null;
  onPick: (id: string) => void;
}) {
  return (
    <>
      <ColumnHeader color={color} role={role} />
      <select
        className="input"
        style={{ width: '100%' }}
        value=""
        onChange={(ev) => {
          if (ev.target.value) onPick(ev.target.value);
        }}
        disabled={grouped.length === 0}
      >
        <option value="">{emptyMessage ?? 'Select an evaluation…'}</option>
        {grouped.map((g) => (
          <optgroup key={g.wfId} label={g.wfName}>
            {g.evs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.id.slice(0, 12)} — {formatDateTime(c.createdAt)}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <p className="t-xs t-muted" style={{ marginTop: 'var(--sp-2)' }}>
        Pick another completed evaluation — same workflow or a different one.
      </p>
    </>
  );
}

/**
 * Side-by-side comparison table for the Score summary section. One column
 * per eval (current + baselines) plus a Δ column per baseline. Reuses the
 * existing pairwise `/compare/:otherId` endpoint — N=2 baselines = 2 calls.
 */
function ScoreSummaryCompareTable({
  currentEvalId,
  baselineEvalIds,
}: {
  currentEvalId: string;
  baselineEvalIds: string[];
}) {
  const client = useClient();
  // Up to two paired queries — hook order stable.
  const b0 = useQuery({
    enabled: !!baselineEvalIds[0],
    queryKey: ['eval-compare', currentEvalId, baselineEvalIds[0]],
    queryFn: () =>
      client.http.get(`v1/evaluations/${currentEvalId}/compare/${baselineEvalIds[0]}`).json<{
        base: { id: string; scores: Record<string, number> };
        candidate: { id: string; scores: Record<string, number> };
        deltas: Record<string, number>;
      }>(),
  });
  const b1 = useQuery({
    enabled: !!baselineEvalIds[1],
    queryKey: ['eval-compare', currentEvalId, baselineEvalIds[1]],
    queryFn: () =>
      client.http.get(`v1/evaluations/${currentEvalId}/compare/${baselineEvalIds[1]}`).json<{
        base: { id: string; scores: Record<string, number> };
        candidate: { id: string; scores: Record<string, number> };
        deltas: Record<string, number>;
      }>(),
  });
  const queries = [b0, b1];

  const firstReady = queries.find((q) => q.data);
  if (!firstReady?.data) return <em className="t-muted">Loading comparison…</em>;

  const allKeys = new Set<string>();
  for (const q of queries) {
    if (q.data) {
      for (const k of Object.keys(q.data.deltas)) allKeys.add(k);
      for (const k of Object.keys(q.data.base.scores)) allKeys.add(k);
    }
  }
  const baseScores = firstReady.data.base.scores;

  const deltaColor = (d: number | undefined) =>
    d == null || Math.abs(d) < 0.005
      ? 'var(--text-muted)'
      : d > 0
        ? 'var(--status-success, #2eb872)'
        : 'var(--status-danger, #e44545)';

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
          <th style={thStyle}>Metric</th>
          <th style={thStyle}>
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 2,
                background: currentSeriesColor,
                marginRight: 6,
                verticalAlign: 'middle',
              }}
            />
            Current
          </th>
          {baselineEvalIds.map((bid, i) => (
            <th key={bid} style={thStyle}>
              <span
                style={{
                  display: 'inline-block',
                  width: 10,
                  height: 2,
                  background: baselineSlotColors[i],
                  marginRight: 6,
                  verticalAlign: 'middle',
                }}
              />
              Baseline {i + 1}
            </th>
          ))}
          {baselineEvalIds.map((bid, i) => (
            <th key={`d-${bid}`} style={thStyle}>
              Δ vs B{i + 1}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from(allKeys).map((key) => (
          <tr
            key={key}
            style={{ borderTop: '1px solid var(--border-subtle, rgba(255,255,255,0.08))' }}
          >
            <td style={tdStyle}>{camelToTitle(key)}</td>
            <td style={tdStyle}>{formatMetric(baseScores[key])}</td>
            {queries.map((q, i) => (
              <td key={`cand-${i}`} style={tdStyle}>
                {q.data ? formatMetric(q.data.candidate.scores[key]) : '—'}
              </td>
            ))}
            {queries.map((q, i) => {
              const d = q.data?.deltas[key];
              const color = deltaColor(d);
              return (
                <td key={`delta-${i}`} style={{ ...tdStyle, color, fontWeight: 600 }}>
                  {d == null ? '—' : `${d > 0 ? '+' : ''}${formatMetric(d)}`}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Node-level headline ────────────────────────────────────────────────────

function NodeLevelHeadline({
  evalId,
  score,
}: {
  evalId: string;
  score: { passed: number; total: number };
}) {
  const hasScoring = score.total > 0;
  const allPassed = hasScoring && score.passed === score.total;
  const rate = hasScoring ? score.passed / score.total : 0;
  const accent = !hasScoring
    ? 'var(--text-secondary)'
    : allPassed
      ? 'var(--status-success, #2eb872)'
      : rate >= 0.5
        ? 'var(--horizon-glow)'
        : 'var(--status-danger, #e44545)';
  return (
    <div
      className="card"
      style={{ marginBottom: 'var(--sp-3)', borderLeft: `4px solid ${accent}` }}
    >
      <div
        className="card-body"
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}
      >
        <div style={{ flex: '0 0 auto', minWidth: 110 }}>
          <div
            className="t-xs t-muted"
            style={{ textTransform: 'uppercase', letterSpacing: '.06em' }}
          >
            Node-level evals
          </div>
          <div
            style={{
              fontSize: 44,
              fontWeight: 'var(--fw-med)',
              lineHeight: 1,
              marginTop: 4,
              color: accent,
            }}
          >
            {hasScoring ? `${(rate * 100).toFixed(0)}%` : '—'}
          </div>
          <div className="t-xs t-muted" style={{ marginTop: 2 }}>
            {hasScoring ? `${score.passed} / ${score.total} checks passed` : 'no checks yet'}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <p className="t-sm" style={{ margin: 0 }}>
            {hasScoring ? (
              <>
                Deep-equal scoring against per-node ground truth from the latest run.
                {allPassed ? ' Every check passed.' : ' Drill down to see which ones failed.'}
              </>
            ) : (
              <>
                No node-level ground truth yet. Author expected outputs per node and the runner will
                deep-equal score each node&apos;s actual output on every eval run.
              </>
            )}
          </p>
        </div>
        <Link
          to={routes.evalNodes(evalId)}
          className={`btn btn--${hasScoring ? 'ghost' : 'primary'} btn--sm`}
        >
          {hasScoring ? 'View per-node →' : 'Author node GTs →'}
        </Link>
      </div>
    </div>
  );
}

// ── Per-entry row (item 5: color-coded backgrounds) ────────────────────────

function PerEntryRow({ result }: { result: IPerEntryResult }) {
  const [open, setOpen] = useState(false);
  const cleanError = result.error && result.error !== '[object Object]' ? result.error : null;
  const cls = classifyEntry(result);
  const rowBg =
    cls === 'passed'
      ? 'rgba(46,184,114,0.06)'
      : cls === 'errored'
        ? 'rgba(229,160,13,0.06)'
        : cls === 'failed'
          ? 'rgba(228,69,69,0.06)'
          : undefined;
  return (
    <>
      <tr
        onClick={() => setOpen((v) => !v)}
        style={{
          borderTop: '1px solid var(--border-subtle, rgba(255,255,255,0.08))',
          cursor: 'pointer',
          background: rowBg,
        }}
      >
        <td style={tdStyle} aria-hidden={true}>
          <span style={{ display: 'inline-block', width: 12 }}>{open ? '▾' : '▸'}</span>
        </td>
        <td style={tdStyle}>
          <div style={{ fontWeight: 600 }}>{result.entryName ?? '—'}</div>
          <code className="t-xs t-muted">{result.entryId?.slice(0, 12)}</code>
        </td>
        <td style={tdStyle}>
          <span
            className={`fk-badge--${
              result.error || result.passed === false ? 'failed' : 'completed'
            }`}
            title={
              result.error
                ? 'workflow run errored'
                : result.passed === false
                  ? 'scoring failed (ground-truth mismatch)'
                  : result.passed === true
                    ? 'scoring passed'
                    : 'workflow run completed'
            }
          >
            {result.error
              ? 'errored'
              : result.passed === false
                ? 'failed'
                : result.passed === true
                  ? 'passed'
                  : 'completed'}
          </span>
        </td>
        <td style={{ ...tdStyle, color: 'var(--status-danger)' }} className="t-xs">
          {cleanError ?? '—'}
        </td>
      </tr>
      {open && result.runId ? (
        <tr style={{ background: 'var(--surface-2)' }}>
          <td colSpan={4} style={{ padding: 'var(--sp-3)' }}>
            <EvalRunDetail result={result} runId={result.runId} groundTruthId={result.entryId} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

// ── Task-based running state (before scoreSummary is populated) ──────────

function TaskBasedRunningState({
  status,
  hasSuite,
}: {
  isRunning: boolean;
  progress: { completed: number; total: number; failed: number } | null;
  status: string;
  hasSuite: boolean;
}) {
  // The EvalDetailShell already renders a progress bar above this content
  // when status=RUNNING and progress is non-null. This component just
  // provides context about the eval type while waiting for results.
  return (
    <div>
      <div className="card" style={{ padding: 24, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <span
            className="badge"
            style={{
              background: 'var(--status-info-bg)',
              color: 'var(--status-info)',
              border: '1px solid var(--status-info-bd)',
              fontSize: 11,
              padding: '2px 8px',
            }}
          >
            TASK-BASED
          </span>
          <span className="t-xs t-muted">{hasSuite ? 'Suite evaluation' : 'Task evaluation'}</span>
        </div>

        <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 500 }}>
          {status === 'RUNNING'
            ? 'Executing trials...'
            : status === 'PENDING'
              ? 'Evaluation queued'
              : status === 'FAILED'
                ? 'Evaluation failed'
                : `Evaluation ${status.toLowerCase()}`}
        </h3>
        <p className="t-muted" style={{ margin: 0, fontSize: 13 }}>
          {status === 'RUNNING'
            ? 'Task results, KPI cards, and reliability curves will appear once all trials complete.'
            : status === 'PENDING'
              ? 'Waiting for the runner to pick up this evaluation.'
              : 'Check the progress bar above for details.'}
        </p>
      </div>
    </div>
  );
}
